import axiosInstance from 'config/axiosInstance';
import { FC, FormEvent, useCallback, useEffect, useState, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useModalDialog } from 'reusable/useModalDialog';
import { secondsToHMS, totalSeconds } from 'reusable/Client/totalSeconds';
import { durationText } from 'reusable/durationText';
import FetchFailed from 'reusable/FetchFailed';
import { useLatestRequest } from 'reusable/useLatestRequest';

interface RemoveTaskDialogProps {
  clientName: string;
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;
  taskId: number | null;
}

interface TaskSession {
  name: string;
  create_time: string;
  update_time: string;
}

const RemoveTaskDialog: FC<RemoveTaskDialogProps> = ({
  clientName,
  isOpen,
  onClose,
  onDelete,
  taskId,
}) => {
  const { t } = useTranslation('client');
  // ADM-16。Escape で閉じる・フォーカスを中に留める・閉じたら開いた操作へ戻す。
  // RV-125。見出しの id は文書の中で一意にする（重なったダイアログが別の見出しで読まれた）。
  const headingId = useId();
  const dialogRef = useModalDialog(isOpen, onClose);
  const [taskSession, setTaskSession] = useState<TaskSession>();
  const [totalDuration, setTotalDuration] = useState<{
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);
  const { id } = useParams<{ id: string }>();

  // EV-318。読み込みの失敗（「読み込み中」のままにしない）と、最後の要求の応答だけを反映するための番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();

  const fetchData = useCallback(async () => {
    const isCurrent = beginRequest();
    setFetchError(null);
    setTaskSession(undefined);
    setTotalDuration(null);
    try {
      const task = await axiosInstance.get(`/v1/tasks/${taskId}/`);
      if (!isCurrent()) return;
      setTaskSession(task.data);
      const hours = await axiosInstance.get(
        `/v1/client_deliveries/${id}/tasks/${taskId}/hours_spent/`
      );
      if (!isCurrent()) return;
      // EV-288。数値の秒（新しい API）も秒の文字列（古い API）も同じ秒として読む。
      setTotalDuration(secondsToHMS(totalSeconds(hours.data.total_hours_spent)));
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Failed to fetch task session data:', error);
      setFetchError(error);
    }
  }, [beginRequest, taskId, id]);

  useEffect(() => {
    if (isOpen && taskId) {
      fetchData();
    }
    // EV-318。閉じた後・別の作業へ切り替えた後に届いた応答は捨てる。
    return () => {
      beginRequest();
    };
  }, [isOpen, taskId, fetchData, beginRequest]);

  if (!isOpen) return null;

  /**
   * ADM-22。この画面の操作は「この配送先から作業を外す」であって、
   * 会社共通の作業マスターの削除ではない。以前は /v1/tasks/{id}/ を
   * DELETEしており、他の配送先からも同じ作業が消えていた。
   *
   * ここでは選択の解除だけを行い、保存は呼び出し元の配送先更新に任せる。
   */
  // EV-325（2026-10-14 PM 判断）。作業名を読めるまで（読み込み中・失敗の間）は外せない。
  // 何を外すかを確かめられないまま操作させない。合計時間の失敗も失敗の知らせが出るので止める。
  const canRemove = Boolean(taskSession) && !fetchError;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // 配送先の編集画面の form の中に描画されるため、外側の form へ送信を伝えない。
    e.stopPropagation();
    // ボタンを止めても Enter の送信は届くので、ここでも止める。
    if (!canRemove) return;
    onDelete();
    onClose();
  };

  return (
    <div className="fixed z-20 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          role="dialog"
          aria-modal="true"
          aria-labelledby={headingId}
          ref={dialogRef}
          tabIndex={-1}
        >
          <div className="bg-white">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <div className="flex justify-between items-center mt-4 mx-6">
                  <h3
                    className="text-subtitle-1 font-medium text-neutral/500"
                    id={headingId}
                  >
                    {t('remove_task.title')}
                  </h3>
                  <button type="button"
                    onClick={() => onClose()}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <span className="sr-only">{t('ui.close', { ns: 'common' })}</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <hr className="border-neutral/50 mt-4 w-full" />
                {/* EV-318。作業・合計時間を読めなかったことを出し、取り直せるようにする。 */}
                {fetchError ? (
                  // PR #268 の照合の P3-3。文がダイアログの端に付かないよう、本文と同じ左右の余白に収める（編集の画面と同じ）。
                  <div className="mx-4 sm:mx-6 text-center">
                    <FetchFailed
                      error={fetchError}
                      onRetry={fetchData}
                      message={t('ui.detail-fetch-failed', { ns: 'common' })}
                    />
                  </div>
                ) : null}
                <p className="m-4 sm:mx-6 sm:mt-6">
                  {t('remove_task.confirm', {
                    task: taskSession?.name ?? '',
                    client: clientName,
                  })}
                  <br /> <br />
                  {t('remove_task.body_1')}
                  {t('remove_task.body_2') && (
                    <>
                      <br /> <br />
                      {t('remove_task.body_2')}
                    </>
                  )}
                </p>
                <form onSubmit={handleSubmit}>
                  <div
                    className={`grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-4 m-4 sm:m-6 sm:rounded-lg border sm:border-neutral/50 sm:p-4`}
                  >
                    <div>
                      <label className="block mb-1 text-overline font-regular text-neutral/100">
                        {t('remove_task.task_name')}
                      </label>
                      <p className="text-body-2 font-normal text-neutral/300 break-words">
                        {taskSession?.name}
                      </p>
                    </div>
                    <div>
                      <label className="block mb-1 text-overline font-regular text-neutral/100">
                        {t('remove_task.task_duration')}
                      </label>
                      <p className="text-body-2 font-normal text-neutral/300 break-words">
                        {totalDuration
                          ? // EV-312。値が数でないと英語の入れ子の文言が崩れるため、共通の関数で「-」にする。
                            durationText(t, 'remove_task.duration_value', {
                              hours: totalDuration.hours,
                              minutes: totalDuration.minutes,
                              seconds: totalDuration.seconds,
                            })
                          : fetchError
                          ? '-'
                          : t('ui.loading', { ns: 'common' })}
                      </p>
                    </div>
                  </div>
                  <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center items-center rounded-md border bg-white text-subtitle-2 text-semibold text-neutral/75"
                      onClick={() => onClose()}
                    >
                      {t('action.cancel')}
                    </button>
                    {/* 送信（handleSubmit）だけで外す。onClick と submit の両方で呼ばれていた。 */}
                    <button
                      type="submit"
                      disabled={!canRemove}
                      className="w-full inline-flex justify-center items-center rounded-md bg-primary/300 text-subtitle-2 text-white h-12 disabled:opacity-60"
                    >
                      {t('action.understand')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoveTaskDialog;
